import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Calendar, Award, Handshake, TrendingUp } from 'lucide-react';

export default function HistoryPage() {
  const timelineEvents = [
    {
      year: '1950s',
      title: 'Commonwealth Sugar Agreement',
      description: 'The roots of our organization can be traced back to the Commonwealth Sugar Agreement, establishing the foundation for sugar trade cooperation.',
      icon: <Handshake className="w-6 h-6" />
    },
    {
      year: '1975',
      title: 'Lomé Convention',
      description: 'The first Lomé Convention established preferential trade arrangements between the EEC and ACP countries, including sugar protocols.',
      icon: <Award className="w-6 h-6" />
    },
    {
      year: '1980s-1990s',
      title: 'Expansion and Growth',
      description: 'Gradual expansion of membership and strengthening of advocacy efforts as global trade patterns evolved.',
      icon: <TrendingUp className="w-6 h-6" />
    },
    {
      year: '2000s',
      title: 'Cotonou Agreement',
      description: 'Transition to the Cotonou Agreement and adaptation to changing EU trade preferences and WTO rules.',
      icon: <Calendar className="w-6 h-6" />
    },
    {
      year: '2020s',
      title: 'Modern Era',
      description: 'Continued advocacy in the post-Brexit landscape and adaptation to contemporary trade challenges.',
      icon: <Award className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">ACP Sugar History</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A journey spanning over seven decades of advocacy, cooperation, and development 
            in the global sugar industry.
          </p>
        </div>

        {/* Historical Context */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Origins and Foundation</h2>
              <p className="text-lg text-gray-600 mb-6">
                The ACP/LDC Sugar Industries Group has its origins in the Commonwealth Sugar Agreement 
                of the 1950s, though elements of cooperation between sugar-producing countries may 
                trace back even earlier to colonial trade arrangements.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The Commonwealth Sugar Agreement was established to provide preferential market access 
                for sugar from Commonwealth countries, creating a framework for cooperation that would 
                evolve into today's organization.
              </p>
              <p className="text-lg text-gray-600">
                From these early beginnings, the organization has continuously adapted to changing 
                global trade landscapes while maintaining its core mission of supporting sugar 
                industries in developing countries.
              </p>
            </div>
            <div className='h-full'>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1615811361523-6bd03d7748e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Vintage sugar processing equipment"
                className="rounded-lg shadow-lg w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-16 relative">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Historical Timeline</h2>

          <div className="relative grid lg:grid-cols-2 auto-rows-min gap-y-12 gap-x-8">
            <div className="absolute top-0 bottom-0 left-1/2 transform -translate-x-1/2 w-0.5 border-l-2 border-dashed border-gray-300 z-0"></div>

            {timelineEvents.map((event, index) => {
              const isLeft = index % 2 === 0; 
              return (
                <div
                  key={event.year}
                  className={`relative z-10 ${isLeft ? 'lg:col-start-1' : 'lg:col-start-2'}`}
                >
                  <Card className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center space-x-4">
                        <div className="bg-amber-100 p-2 rounded-full text-amber-600">
                          {event.icon}
                        </div>
                        <div>
                          <CardTitle className="text-amber-600">{event.year}</CardTitle>
                          <CardTitle>{event.title}</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-lg">{event.description}</CardDescription>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </section>


        {/* Key Milestones */}
        <section className="mb-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Key Milestones</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-amber-600 w-40 h-40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1950s</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Foundation Era</h3>
              <p className="text-gray-600">
                Establishment of preferential trade arrangements and the foundation of 
                cooperative frameworks between sugar-producing nations.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-emerald-600 w-40 h-40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1970s</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Lomé Conventions</h3>
              <p className="text-gray-600">
                Integration into the broader ACP-EU partnership framework, securing 
                preferential access to European markets for sugar exports.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-fuchsia-600 w-40 h-40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2000s</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Modern Challenges</h3>
              <p className="text-gray-600">
                Adaptation to WTO rules, EU sugar reform, and changing global trade 
                dynamics while maintaining advocacy for member interests.
              </p>
            </div>
          </div>
        </section>

        {/* Legacy and Impact */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className='h-full'>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Modern sugar processing facility"
                className="rounded-lg shadow-lg w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Legacy and Impact</h2>
              <p className="text-lg text-gray-600 mb-6">
                Over the decades, the ACP/LDC Sugar Industries Group has played a crucial role 
                in ensuring that sugar industries in developing countries maintain access to 
                vital export markets, supporting millions of livelihoods.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our advocacy has helped shape international trade policies, influenced development 
                cooperation programs, and supported the modernization of sugar industries across 
                member countries.
              </p>
              <p className="text-lg text-gray-600">
                Today, we continue this legacy while adapting to new challenges including climate 
                change, sustainability requirements, and evolving consumer preferences.
              </p>
            </div>
          </div>
        </section>

        {/* Looking Forward */}
        <section className="text-center bg-amber-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Looking Forward</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            As we move into the future, the ACP/LDC Sugar Industries Group remains committed to 
            its founding principles while embracing innovation, sustainability, and inclusive 
            development. Our rich history serves as the foundation for continued advocacy and 
            support for sugar industries across our member countries.
          </p>
        </section>
      </div>
    </div>
  );
}